import { useAtom } from 'jotai';
import React, { useEffect, useState } from 'react';
import { PiXBold } from 'react-icons/pi';
import { ActionIcon, Button, Loader, Title } from 'rizzui';
import { formsSubmitAtom } from '../../atom/atom';

const Drawer = React.lazy(() => import('rizzui').then(module => ({ default: module.Drawer })));

export function FormDrawerView({
    isOpen,
    drawerTitle,
    subtitleText,
    setOpenDrawer,
    children,
    showMoreButton = true,
    submitText = 'Guardar',
    closeOnClickOutside = false
}: React.PropsWithChildren<{
    submitText?: string;
    drawerTitle?: string;
    subtitleText?: string;
    hasSearched?: boolean;
    setOpenDrawer: React.Dispatch<React.SetStateAction<boolean>>;
    isOpen?: boolean;
    showMoreButton?: boolean
    closeOnClickOutside?: boolean;
}>) {

    // STATES

    const [isComponentLoaded, setComponentLoaded] = useState(false);
    const [isSubmitting] = useAtom(formsSubmitAtom);
    const formilRef = React.useRef<HTMLFormElement>(null);

    // USE EFFECTS

    useEffect(() => {
        setComponentLoaded(true);
    }, []);

    // RENDER

    return (
        <React.Suspense fallback={<div className='text-center'><Loader size='sm' /></div>}>
            {isComponentLoaded && (
                <Drawer
                    size="md"
                    onClose={() => { closeOnClickOutside && setOpenDrawer(false) } }
                    isOpen={isOpen ?? false}
                    overlayClassName="dark:bg-opacity-40 dark:backdrop-blur-[3px]"
                    containerClassName="dark:bg-gray-100 overflow-auto-important drawer-container"
                    className="z-[1000]"
                >
                    <div className="flex flex-col min-h-screen justify-between p-5 overflow-x-hidden" >
                        <div>
                            <div className="-mx-5 mb-6 flex items-center justify-between border-b border-muted px-5 pb-4">
                                <Title as="h4">
                                    <span>{drawerTitle}</span>
                                    {subtitleText && ( <>
                                        <br />
                                        <span className="text-[10px] text-[#6b7280]">
                                            {subtitleText}
                                        </span>
                                    </> )}
                                </Title>
                                <ActionIcon
                                    size="sm"
                                    rounded="full"
                                    variant="text"
                                    title={'Close Filter'}
                                    onClick={() => setOpenDrawer(false)}
                                >
                                    <PiXBold className="h-4 w-4" />
                                </ActionIcon>
                            </div>
                            <div className="flex-grow">
                                <div className="grid grid-cols-1 gap-6 [&_.price-field>span.mr-2]:mb-1.5 [&_.price-field]:flex-col [&_.price-field]:items-start [&_.react-datepicker-wrapper]:w-full [&_.react-datepicker-wrapper_.w-72]:w-full [&_.text-gray-500]:text-gray-700 [&_button.h-9]:h-10 sm:[&_button.h-9]:h-11 [&_label>.h-9]:h-10 sm:[&_label>.h-9]:h-11 [&_label>.w-24.h-9]:w-full">
                                    {React.isValidElement(children) 
                                        ? React.cloneElement(children as React.ReactElement, { formRef: formilRef })
                                        : children}
                                </div>
                            </div>
                        </div>
                        {showMoreButton && (  
                            <div className='flex justify-between gap-2'>
                                <Button
                                    size="lg"
                                    variant='outline'
                                    disabled={isSubmitting}
                                    onClick={() => setOpenDrawer(false)}
                                    className="mt-5 h-11 w-full text-sm border-black"
                                >
                                    Cancelar
                                </Button>
                                <Button
                                    size="lg"
                                    disabled={isSubmitting}
                                    onClick={() => {
                                        if (formilRef.current) {
                                            formilRef.current?.requestSubmit();
                                        }
                                    }}
                                    type="submit"
                                    className="mt-5 h-11 w-full text-sm"
                                >
                                    {isSubmitting ? <Loader size='sm' /> : submitText}
                                </Button>
                            </div>
                        )}
                    </div>
                </Drawer>
            )}
        </React.Suspense>
    );
}

export type FormDrawerProps = {
    searchTerm: string;
    onSearchClear: () => void;
    onSearchChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    columns: { [key: string]: any }[];
    checkedColumns: string[];
    setCheckedColumns: React.Dispatch<React.SetStateAction<string[]>>;
    hideIndex?: number;
    children?: React.ReactNode;
    drawerTitle?: string;
    hasSearched?: boolean;
    showSearchOnTheRight?: boolean;
    enableDrawerFilter?: boolean;
    menu?: React.ReactNode;
};